import { format } from 'date-fns';
import { CHART_TOOLTIP_STYLE } from '@/utils/chartStyles';

interface TooltipPayloadEntry {
  name: string;
  value: number | null | undefined;
  color: string;
  dataKey: string;
  payload?: Record<string, unknown>;
}

interface ChartTooltipContentProps {
  active?: boolean;
  payload?: TooltipPayloadEntry[];
  label?: number;
  /** Format a value for display, e.g. (value, name) => "25.50°C" */
  valueFormatter?: (value: number, name: string) => string;
  /** Format a series name for display, e.g. "ca_mastertronic" => "Ca (Mast)" */
  nameFormatter?: (name: string) => string;
}

export function ChartTooltipContent({
  active,
  payload,
  label,
  valueFormatter,
  nameFormatter,
}: ChartTooltipContentProps) {
  if (!active || !payload || payload.length === 0 || label == null) return null;

  // Validate timestamp before formatting
  const date = new Date(label);
  const isValidDate = !isNaN(date.getTime());

  // Filter out timestamp entries and non-finite numeric values
  const validEntries = payload.filter(
    (entry) =>
      entry.dataKey !== 'timestamp' &&
      entry.value != null &&
      typeof entry.value === 'number' &&
      isFinite(entry.value)
  );

  if (validEntries.length === 0) return null;

  return (
    <div style={CHART_TOOLTIP_STYLE}>
      <div style={{ fontWeight: 600, marginBottom: 4, fontSize: '11px', opacity: 0.7 }}>
        {isValidDate ? format(date, 'MMM d, yyyy  h:mm a') : 'Unknown date'}
      </div>
      {validEntries.map((entry, index) => {
        const note = entry.payload?.[`${entry.dataKey}_note`] as string | undefined;
        return (
          <div key={index}>
            <div
              style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}
            >
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: entry.color,
                  flexShrink: 0,
                }}
              />
              <span style={{ opacity: 0.8 }}>
                {nameFormatter ? nameFormatter(entry.name) : entry.name}:
              </span>
              <span style={{ fontWeight: 600 }}>
                {valueFormatter
                  ? valueFormatter(entry.value as number, entry.name)
                  : (entry.value as number).toFixed(2)}
              </span>
            </div>
            {note && (
              <div style={{ fontSize: '10px', opacity: 0.7, fontStyle: 'italic', marginLeft: 14, marginTop: 1, maxWidth: 250 }}>
                {note}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
